import { Label, Switch } from '@chain-zen/ui'
import { toast } from 'sonner'

import type { IDateTime } from '../../../../../types/post'
import { DateTimeField } from '../../date-time-field'

interface AppointmentSectionProps {
  appointment: {
    end: IDateTime | null
    isFirstComeFirstServe: boolean
    start: IDateTime | null
  } | null
  onUpdate: (appointment: { end: IDateTime | null; isFirstComeFirstServe: boolean; start: IDateTime | null }) => void
}

const isCompleteDateTime = (dt: IDateTime | null): boolean => {
  return !!dt && !!dt.date && !!dt.time
}

const validateDateTime = (start: IDateTime, end: IDateTime): boolean => {
  // Only validate if both start and end have complete date and time
  if (
    !isCompleteDateTime({ date: start.date, time: start.time }) ||
    !isCompleteDateTime({ date: end.date, time: end.time })
  ) {
    return true
  }

  const startStr = `${start.date}T${start.time}`
  const endStr = `${end.date}T${end.time}`

  return startStr < endStr
}

export function AppointmentSection({ appointment, onUpdate }: AppointmentSectionProps) {
  const handleStartChange = (start: IDateTime | null) => {
    if (start && appointment?.end) {
      // Only validate if both dates have complete information
      if (isCompleteDateTime(start) && isCompleteDateTime(appointment.end)) {
        if (!validateDateTime(start, appointment.end)) {
          toast.error('Start date and time must be earlier than end date and time')
          // Reset end time when complete datetime is invalid
          onUpdate({
            ...appointment,
            end: null,
            isFirstComeFirstServe: appointment.isFirstComeFirstServe || false,
            start
          })
          return
        }
      }
    }

    onUpdate({
      ...appointment,
      end: appointment?.end || null,
      isFirstComeFirstServe: appointment?.isFirstComeFirstServe || false,
      start
    })
  }

  const handleEndChange = (end: IDateTime | null) => {
    if (end && appointment?.start) {
      // Only validate if both dates have complete information
      if (isCompleteDateTime(appointment.start) && isCompleteDateTime(end)) {
        if (!validateDateTime(appointment.start, end)) {
          toast.error('End date and time must be later than start date and time')
          return
        }
      }
    }

    onUpdate({
      ...appointment,
      end,
      isFirstComeFirstServe: appointment?.isFirstComeFirstServe || false,
      start: appointment?.start || null
    })
  }

  const handleFCFSChange = (checked: boolean) => {
    onUpdate({
      ...appointment,
      end: checked ? null : appointment?.end || null,
      isFirstComeFirstServe: checked,
      start: appointment?.start || null
    })
  }

  return (
    <div className="space-y-1.5">
      {/* FCFS Toggle - Place at top for better UX */}
      <div className="flex items-center gap-2">
        <Switch id="fcfs" checked={appointment?.isFirstComeFirstServe || false} onCheckedChange={handleFCFSChange} />
        <Label htmlFor="fcfs" className="text-xs cursor-pointer select-none">
          First Come First Serve
        </Label>
      </div>

      {/* Date/Time Fields Container */}
      <div className="space-y-1.5">
        {/* Start Date */}
        <div className="space-y-1">
          <Label className="text-xs font-medium">Start Date Time</Label>
          <DateTimeField dateTime={appointment?.start || null} onUpdate={handleStartChange} />
        </div>

        {/* End Date - Only show when FCFS is enabled */}
        {!!appointment?.isFirstComeFirstServe && (
          <div className="space-y-1">
            <Label className="text-xs font-medium">End Date Time</Label>
            <DateTimeField dateTime={appointment?.end || null} onUpdate={handleEndChange} />
          </div>
        )}
      </div>
    </div>
  )
}
